/**
 * Lochkreis-Rechner: aus einer Messung am Rad den Lochkreis bestimmen und dem
 * naechsten gaengigen Lochkreis (und der passenden Kollektion) zuordnen.
 *
 * Formel (Definition, am 31.08.2026 gegen Wikipedia „Lochkreis" geprueft):
 *   Lochkreis A = s / sin(180° / n)
 * mit s = Abstand benachbarter Lochmitten und n = Lochzahl. Faktoren:
 * 4 Loch s/0,7071 · 5 Loch s/0,5878 · 6 Loch s/0,5 · 3 Loch s/0,8660 ·
 * 8 Loch s/0,3827. Bei gerader Lochzahl ist der Abstand gegenueberliegender
 * Lochmitten direkt der Lochkreis.
 *
 * Die Zuordnung zum Standardmass ist bewusst vorsichtig: 5x110, 5x112 und
 * 5x114,3 liegen im Nachbarabstand nur je 1,2 bis 1,4 mm auseinander. Liegt
 * die Messung naeher als 0,5 mm an der Grenze zwischen zwei Sollwerten,
 * zeigt der Rechner beide Kandidaten und bittet um Nachmessen mit dem
 * Messschieber, statt eine falsche Sicherheit vorzutaeuschen.
 */
(function () {
  'use strict';

  var GRENZEN = { abstand: { min: 20, max: 300 } };
  var LOCHZAHLEN = [3, 4, 5, 6, 8];

  /**
   * Gaengige Lochkreise. `handle` nur dort, wo zrshop eine Kollektion fuehrt
   * (Sitemap am 31.08.2026: 22 Lochkreis-Kollektionen). Ohne Handle wird
   * auf das Felgensortiment verlinkt.
   */
  var STANDARD = [
    { n: 3, pcd: 112 },
    { n: 4, pcd: 98, handle: 'felgen-4x98' }, { n: 4, pcd: 100, handle: 'felgen-4x100' },
    { n: 4, pcd: 108, handle: 'felgen-4x108' }, { n: 4, pcd: 110 }, { n: 4, pcd: 114.3 }, { n: 4, pcd: 130 },
    { n: 5, pcd: 98 }, { n: 5, pcd: 100, handle: 'felgen-5x100' }, { n: 5, pcd: 105, handle: 'felgen-5x105' },
    { n: 5, pcd: 108, handle: 'felgen-5x108' }, { n: 5, pcd: 110, handle: 'felgen-5x110' },
    { n: 5, pcd: 112, handle: 'felgen-5x112' }, { n: 5, pcd: 114.3, handle: 'felgen-5x114-3' },
    { n: 5, pcd: 115, handle: 'felgen-5x115' }, { n: 5, pcd: 118, handle: 'felgen-5x118' },
    { n: 5, pcd: 120, handle: 'felgen-5x120' }, { n: 5, pcd: 120.65 }, { n: 5, pcd: 127, handle: 'felgen-5x127' },
    { n: 5, pcd: 130, handle: 'felgen-5x130' }, { n: 5, pcd: 132, handle: 'felgen-5x132' },
    { n: 5, pcd: 139.7, handle: 'felgen-5x139-7' }, { n: 5, pcd: 150 }, { n: 5, pcd: 160, handle: 'felgen-5x160' }, { n: 5, pcd: 165.1 },
    { n: 6, pcd: 114.3, handle: 'felgen-6x114-3' }, { n: 6, pcd: 115 }, { n: 6, pcd: 120, handle: 'felgen-6x120' },
    { n: 6, pcd: 127 }, { n: 6, pcd: 130, handle: 'felgen-6x130' }, { n: 6, pcd: 135, handle: 'felgen-6x135' },
    { n: 6, pcd: 139.7, handle: 'felgen-6x139-7' }, { n: 6, pcd: 170 }, { n: 6, pcd: 180 },
    { n: 8, pcd: 165.1 }, { n: 8, pcd: 170 },
  ];

  /* ----------------------------------------------------------- Rechenkern */

  function faktor(n) {
    return Math.sin(Math.PI / n);
  }

  /** Lochkreis aus dem Abstand benachbarter Lochmitten. */
  function ausNachbar(s, n) {
    return s / faktor(n);
  }

  /** Erwarteter Nachbarabstand fuer einen Lochkreis (zum Nachmessen). */
  function nachbarAbstand(pcd, n) {
    return pcd * faktor(n);
  }

  function istGueltig(n, s) {
    return LOCHZAHLEN.indexOf(n) >= 0 && Number.isFinite(s) && s >= GRENZEN.abstand.min && s <= GRENZEN.abstand.max;
  }

  /**
   * messart 'nachbar' = Abstand benachbarter Lochmitten (jede Lochzahl),
   * 'gegenueber' = Abstand gegenueberliegender Lochmitten (nur gerade Lochzahl).
   */
  function bestimmen(n, s, messart) {
    var pcd = messart === 'gegenueber' ? s : ausNachbar(s, n);
    var kandidaten = STANDARD.filter(function (k) { return k.n === n; })
      .map(function (k) { return { n: k.n, pcd: k.pcd, handle: k.handle || null, diff: pcd - k.pcd }; })
      .sort(function (a, b) { return Math.abs(a.diff) - Math.abs(b.diff); });
    var bester = kandidaten[0] || null;
    var zweiter = kandidaten[1] || null;
    // Mehrdeutigkeit wird im GEMESSENEN Mass beurteilt, nicht im Lochkreis:
    // bei 5 Loch liegen 5x110, 5x112 und 5x114,3 im Nachbarabstand nur je
    // 1,2 bis 1,4 mm auseinander. Massgeblich ist der Abstand der Messung
    // zur Entscheidungsgrenze (Mitte zwischen den beiden naechsten
    // Sollwerten). Unter 0,5 mm (Messschieber-Genauigkeit) zeigen wir beide.
    var mehrdeutig = false;
    if (bester && zweiter && Math.abs(zweiter.diff) <= 3) {
      var sollBest = messart === 'gegenueber' ? bester.pcd : nachbarAbstand(bester.pcd, n);
      var sollZweit = messart === 'gegenueber' ? zweiter.pcd : nachbarAbstand(zweiter.pcd, n);
      var marge = Math.abs(s - (sollBest + sollZweit) / 2);
      mehrdeutig = marge < (messart === 'gegenueber' ? 1.0 : 0.5);
    }
    var unbekannt = !bester || Math.abs(bester.diff) > 3;
    return { pcd: pcd, bester: bester, zweiter: zweiter, mehrdeutig: mehrdeutig, unbekannt: unbekannt };
  }

  function einordnen(e) {
    if (e.unbekannt) {
      return {
        stufe: 'hoch',
        titel: 'Kein gängiger Lochkreis in der Nähe',
        text: 'Der gemessene Wert liegt mehr als drei Millimeter von jedem üblichen Lochkreis entfernt. Meist stimmt dann die Messung nicht: von Lochmitte zu Lochmitte messen, oder von der linken Kante eines Lochs zur linken Kante des Nachbarlochs. Bei Unsicherheit steht der Lochkreis in den Fahrzeugpapieren oder auf der Felgenrückseite.',
      };
    }
    if (e.mehrdeutig) {
      return {
        stufe: 'mittel',
        titel: 'Zwei Lochkreise kommen in Frage',
        text: 'Die beiden nächsten Standardmaße liegen so dicht beieinander, dass eine Messtoleranz von ein bis zwei Millimetern über die Zuordnung entscheidet. Bitte mit einem Messschieber nachmessen und mit den erwarteten Abständen unten vergleichen. Verbindlich sind die Fahrzeugpapiere und die Angabe auf der Felge.',
      };
    }
    return {
      stufe: 'gut',
      titel: 'Eindeutige Zuordnung',
      text: 'Die Messung passt klar zu einem gängigen Lochkreis. Ob eine Felge an Ihr Fahrzeug passt, entscheiden zusätzlich Nabenbohrung, Einpresstiefe, Traglast und die Freigabe des Rades.',
    };
  }

  /* ------------------------------------------------------------- Darstellung */

  function zahl(wert, stellen) {
    return wert.toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
  }

  function pcdText(k) {
    return k.n + 'x' + zahl(k.pcd, k.pcd % 1 ? 1 : 0);
  }

  function linkZu(k) {
    return k && k.handle ? '/collections/' + k.handle : '/pages/felgensortiment';
  }

  /* ------------------------------------------------------------------ Widget */

  function init(wurzel) {
    var fLochzahl = wurzel.querySelector('[data-lr="lochzahl"]');
    var fMessart = wurzel.querySelector('[data-lr="messart"]');
    var fAbstand = wurzel.querySelector('[data-lr="abstand"]');
    var ausgabe = wurzel.querySelector('[data-lr="ausgabe"]');
    var hinweis = wurzel.querySelector('[data-lr="hinweis"]');
    if (!ausgabe || !fLochzahl || !fAbstand) return;

    function setzen(name, text) {
      var el = wurzel.querySelector('[data-lr="' + name + '"]');
      if (el) el.textContent = text;
    }

    function messartWert() {
      var n = parseInt(fLochzahl.value, 10);
      var m = fMessart ? fMessart.value : 'nachbar';
      // Gegenueberliegende Lochmitten gibt es nur bei gerader Lochzahl.
      if (m === 'gegenueber' && n % 2 !== 0) {
        if (fMessart) fMessart.value = 'nachbar';
        m = 'nachbar';
      }
      if (fMessart) {
        var opt = fMessart.querySelector('option[value="gegenueber"]');
        if (opt) opt.disabled = n % 2 !== 0;
      }
      return m;
    }

    function rechne() {
      var n = parseInt(fLochzahl.value, 10);
      var s = parseFloat(fAbstand.value);
      var messart = messartWert();
      if (!istGueltig(n, s)) {
        ausgabe.hidden = true;
        if (hinweis) {
          hinweis.hidden = false;
          hinweis.textContent = 'Bitte Lochzahl wählen und den gemessenen Abstand in Millimetern eintragen (' + GRENZEN.abstand.min + ' bis ' + GRENZEN.abstand.max + ' mm).';
        }
        return;
      }
      if (hinweis) hinweis.hidden = true;

      var e = bestimmen(n, s, messart);
      var urteil = einordnen(e);
      setzen('urteil-titel', urteil.titel);
      setzen('urteil-text', urteil.text);
      var karte = wurzel.querySelector('[data-lr="urteil"]');
      if (karte) karte.setAttribute('data-stufe', urteil.stufe);

      setzen('wert-eingabe', n + ' Loch, ' + zahl(s, 1) + ' mm ' + (messart === 'gegenueber' ? 'gegenüber' : 'benachbart'));
      setzen('wert-pcd', zahl(e.pcd, 1) + ' mm');
      setzen('wert-standard', e.bester && !e.unbekannt ? pcdText(e.bester) : '–');
      setzen('wert-standard-zusatz', e.bester && !e.unbekannt ? 'Abweichung ' + (e.bester.diff >= 0 ? '+' : '−') + zahl(Math.abs(e.bester.diff), 1) + ' mm' : 'kein Standardmaß im Bereich');
      setzen('wert-nachbar', e.bester && !e.unbekannt ? zahl(nachbarAbstand(e.bester.pcd, n), 1) + ' mm' : '–');
      setzen('wert-zweiter', e.zweiter && Math.abs(e.zweiter.diff) <= 6 ? pcdText(e.zweiter) : '–');
      setzen('wert-zweiter-zusatz', e.zweiter && Math.abs(e.zweiter.diff) <= 6 ? 'Nachbarabstand ' + zahl(nachbarAbstand(e.zweiter.pcd, n), 1) + ' mm' : 'kein weiterer Kandidat');
      setzen('wert-faktor', '× ' + zahl(1 / faktor(n), 4));

      var link = wurzel.querySelector('[data-lr="link-primaer"]');
      if (link) {
        if (e.bester && !e.unbekannt) {
          link.href = linkZu(e.bester);
          link.textContent = e.bester.handle ? 'Felgen mit Lochkreis ' + pcdText(e.bester) + ' ansehen' : 'Felgensortiment ansehen';
        } else {
          link.href = '/pages/felgen-konfigurator';
          link.textContent = 'Lochkreis über das Fahrzeug finden';
        }
      }
      var link2 = wurzel.querySelector('[data-lr="link-zweiter"]');
      if (link2) {
        if (e.mehrdeutig && e.zweiter) {
          link2.hidden = false;
          link2.href = linkZu(e.zweiter);
          link2.textContent = e.zweiter.handle ? 'oder ' + pcdText(e.zweiter) + ' ansehen' : 'Felgensortiment ansehen';
        } else {
          link2.hidden = true;
        }
      }
      ausgabe.hidden = false;
    }

    [fLochzahl, fMessart, fAbstand].forEach(function (f) {
      if (f) { f.addEventListener('input', rechne); f.addEventListener('change', rechne); }
    });

    wurzel.querySelectorAll('[data-lr-beispiel]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        fLochzahl.value = btn.getAttribute('data-n');
        if (fMessart) fMessart.value = btn.getAttribute('data-messart') || 'nachbar';
        fAbstand.value = btn.getAttribute('data-s');
        rechne();
        ausgabe.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    });

    rechne();
  }

  function start() {
    document.querySelectorAll('[data-zr-lochkreisrechner]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();

  window.zrLochkreisRechner = { ausNachbar: ausNachbar, nachbarAbstand: nachbarAbstand, bestimmen: bestimmen, einordnen: einordnen, STANDARD: STANDARD };
})();
